/**
 * ComponentDetailCard — standalone per-component detail card injected into the
 * chat thread when a user clicks a row in ChatScoreBreakdownCard
 * (HANDOFF_CHAT_COMPONENT_DETAIL_CARDS_V2). Registered as `component_detail`.
 *
 * Reads its payload ({compKey, fields, details, score}) from the tool result.
 * `fields` is the card-relevant extraction subset (Evidence-V2-wrapped; read
 * via the componentDetail helpers). The insight line is DETERMINISTIC (never
 * LLM). Hard rules: human labels only, no headroom, no cohort n, no weight %,
 * no letter grades, no forward cost estimates, positional not evaluative.
 */
import type { ReactNode } from 'react';

import { FinancialNumber, ScoreDisplay } from '../../../components/ui';
import {
  COMPS,
  type CKey,
} from '../../../components/analysis/CompCard/compConfig';
import type { ComponentDetails } from '../../../api/types';
import { useChatStore } from '../../../stores/chatStore';
import type { ToolResultProps } from '../lib/component-registry';
import {
  f,
  fmtPct,
  cleanBaseRate,
  numberOf,
  positionFromPercentile,
  getComponentInsight,
  hasMinimumData,
  DEGRADATION_TEXT,
  detectedFees,
  totalFeesEur,
  financialCovenantRows,
  operationalCovenantRows,
  testingFrequency,
  securityItems,
  isUnsecured,
  hasPersonalGuarantee,
  type Fields,
  type MarketPosition,
} from '../lib/componentDetail';

interface ComponentDetailResult {
  compKey: CKey;
  /** Source term sheet — namespaces the dom id + dedupe so two deals'
   *  breakdowns in one session don't collide on a bare compKey. */
  tsId: number | string | null;
  fields: Fields;
  details: ComponentDetails | null;
  score: number | null;
}

type Comp = (typeof COMPS)[number];

function capFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function ComponentDetailCard({ result }: ToolResultProps) {
  const setInputDraft = useChatStore((s) => s.setInputDraft);
  const r = (result ?? {}) as Partial<ComponentDetailResult>;
  const compKey = r.compKey;
  const comp = compKey ? COMPS.find((c) => c.key === compKey) : undefined;
  if (!compKey || !comp) {return null;}

  const fields = r.fields ?? null;
  const details = r.details ?? null;
  const score = typeof r.score === 'number' ? r.score : null;
  const accent = comp.compColor.css;
  const domId = `detail-card-${r.tsId ?? 'x'}-${compKey}`;

  if (!hasMinimumData(compKey, fields)) {
    return (
      <div id={domId} className="animate-card-enter mt-2 border border-border bg-surface-card">
        <Header comp={comp} score={score} accent={accent} />
        <p className="px-4 py-3 text-body-sm text-txt-secondary">{DEGRADATION_TEXT[compKey]}</p>
      </div>
    );
  }

  const insight = getComponentInsight(compKey, fields, details);
  const pos = positionFromPercentile(details?.percentile);
  const percentile = typeof details?.percentile === 'number' ? details.percentile : null;

  return (
    <div id={domId} className="animate-card-enter mt-2 border border-border bg-surface-card">
      <Header comp={comp} score={score} accent={accent} />
      <div className="px-4 py-3">
        {insight && (
          <p className="border-l-2 pl-3 text-body-sm font-semibold text-txt-primary" style={{ borderColor: accent }}>
            {insight}
          </p>
        )}
        <div className="mt-3">
          {compKey === 'pricing' && <PricingBody fields={fields} pos={pos} percentile={percentile} />}
          {compKey === 'fee_load' && <FeesBody fields={fields} pos={pos} percentile={percentile} />}
          {compKey === 'structure' && <StructureBody fields={fields} />}
          {compKey === 'conditions' && <ConditionsBody fields={fields} />}
          {compKey === 'security' && <SecurityBody fields={fields} />}
        </div>
        <Pills pills={pillsFor(compKey, pos, fields)} onPick={setInputDraft} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------
function Header({ comp, score, accent }: { comp: Comp; score: number | null; accent: string }) {
  const Icon = comp.Icon;
  return (
    <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
      <div className="flex items-center gap-2">
        <span className="shrink-0" style={{ color: accent }}>
          <Icon size={16} />
        </span>
        <p className="text-caption font-semibold uppercase tracking-wide text-txt-secondary">{comp.label}</p>
      </div>
      {score !== null && <ScoreDisplay score={Math.round(score)} size="sm" showLabel={false} />}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2 last:border-b-0">
      <span className="text-body-sm text-txt-secondary">{label}</span>
      <span className="font-mono text-body-sm text-txt-primary">{children}</span>
    </div>
  );
}

function PositionBar({ percentile }: { percentile: number | null }) {
  if (percentile === null) {return null;}
  const p = Math.max(0, Math.min(100, percentile));
  const color =
    p <= 30 ? 'rgb(var(--success))' : p <= 50 ? 'rgb(var(--teal))' : p <= 70 ? 'rgb(var(--warning))' : 'rgb(var(--error))';
  return (
    <span className="relative inline-block h-1 w-16 bg-surface-subtle align-middle">
      <span className="absolute inset-y-0 left-0" style={{ width: `${p}%`, backgroundColor: color }} />
    </span>
  );
}

function PositionRow({ pos, percentile }: { pos: MarketPosition | null; percentile: number | null }) {
  if (!pos) {return null;}
  return (
    <div className="flex items-center justify-between border-b border-border py-2 last:border-b-0">
      <span className="text-body-sm text-txt-secondary">vs market</span>
      <span className="flex items-center gap-2">
        <PositionBar percentile={percentile} />
        <span className="text-body-sm text-txt-primary">{capFirst(pos)}</span>
      </span>
    </div>
  );
}

function FormulaBlock({ label, value, sub, accent }: { label: string; value: ReactNode; sub?: string; accent?: boolean }) {
  return (
    <div className={`flex-1 px-3 py-2 ${accent ? 'border-y-2 border-orange bg-orange/5 sm:border-x-2 sm:border-y-0' : 'bg-surface-subtle'}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${accent ? 'text-orange' : 'text-txt-tertiary'}`}>{label}</div>
      <div className={`mt-0.5 font-mono text-lg font-semibold ${accent ? 'text-orange' : 'text-txt-primary'}`}>{value}</div>
      {sub && <div className="mt-0.5 text-caption text-txt-tertiary">{sub}</div>}
    </div>
  );
}

function FormulaOp({ op }: { op: string }) {
  return <div className="flex items-center justify-center py-1 font-mono text-txt-tertiary sm:w-7 sm:py-0">{op}</div>;
}

function Pills({ pills, onPick }: { pills: string[]; onPick: (text: string) => void }) {
  if (pills.length === 0) {return null;}
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {pills.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPick(p)}
          className="border border-border px-3 py-1.5 text-caption text-txt-secondary transition-colors hover:border-orange hover:text-orange"
        >
          {p}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------
function PricingBody({ fields, pos, percentile }: { fields: Fields; pos: MarketPosition | null; percentile: number | null }) {
  const rateType = String(f(fields, 'rate_type') ?? '').toLowerCase();
  const base = cleanBaseRate(f(fields, 'base_rate'));
  const margin = numberOf(f(fields, 'margin_bps'));
  const allIn = fmtPct(f(fields, 'all_in_rate_pct'));
  const floor = fmtPct(f(fields, 'rate_floor_pct'));
  const isFixed = rateType === 'fixed' || (!base && margin === null);

  return (
    <>
      {isFixed ? (
        <div className="flex">
          <FormulaBlock label="All-in rate" value={allIn ?? 'Fixed'} sub="Fixed rate" accent />
        </div>
      ) : (
        <div className="flex flex-col gap-1 sm:flex-row sm:items-stretch sm:gap-0">
          <FormulaBlock label="Base rate" value={base || '—'} sub="Reference rate" />
          <FormulaOp op="+" />
          <FormulaBlock label="Margin" value={margin !== null ? `${margin} bps` : '—'} sub="Bank's pricing" accent />
          <FormulaOp op="=" />
          <FormulaBlock label="All-in rate" value={allIn ?? '—'} sub={rateType ? `${capFirst(rateType)} rate` : undefined} />
        </div>
      )}
      <div className="mt-3">
        {rateType && <Row label="Rate type">{capFirst(rateType)}</Row>}
        {floor && <Row label="Rate floor">{floor}</Row>}
        <PositionRow pos={pos} percentile={percentile} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Fees
// ---------------------------------------------------------------------------
function FeesBody({ fields, pos, percentile }: { fields: Fields; pos: MarketPosition | null; percentile: number | null }) {
  const items = detectedFees(fields);
  const total = totalFeesEur(fields);
  const facility = numberOf(f(fields, 'facility_amount'));
  const pctOfFacility = total !== null && facility !== null && facility > 0 ? (total / facility) * 100 : null;

  return (
    <>
      <div className="bg-surface-subtle px-3 py-3">
        <div className="font-mono text-2xl font-semibold text-orange">
          {total !== null ? <FinancialNumber value={total} format="currency" /> : '—'}
        </div>
        <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-txt-tertiary">Total upfront fees</div>
        {pctOfFacility !== null && (
          <div className="mt-0.5 text-caption text-txt-tertiary">{fmtPct(pctOfFacility)} of facility</div>
        )}
      </div>
      <div className="mt-3">
        {items.map((it, i) => (
          <Row key={`${i}-${it.label}`} label={it.label}>
            {it.eur !== null && <FinancialNumber value={it.eur} format="currency" />}
            {it.eur !== null && it.note ? ' · ' : ''}
            {it.note && <span className={it.eur === null ? 'text-txt-secondary' : undefined}>{it.note}</span>}
          </Row>
        ))}
        <PositionRow pos={pos} percentile={percentile} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Structure
// ---------------------------------------------------------------------------
function StructureBody({ fields }: { fields: Fields }) {
  const tenorMonths = numberOf(f(fields, 'tenor_months')) ?? numberOf(f(fields, 'maturity_months'));
  const repayment = String(f(fields, 'repayment_type') ?? '');
  const frequency = String(f(fields, 'repayment_frequency') ?? '').replace(/_/g, '-');
  const grace = numberOf(f(fields, 'grace_period_months'));
  const prepayAllowed = f<boolean>(fields, 'prepayment_allowed_bool');
  const penalties = [
    numberOf(f(fields, 'prepayment_penalty_year_1')),
    numberOf(f(fields, 'prepayment_penalty_year_2')),
    numberOf(f(fields, 'prepayment_penalty_year_3')),
  ].filter((p): p is number => p !== null);

  const tenorLabel =
    tenorMonths !== null
      ? tenorMonths % 12 === 0
        ? `${tenorMonths / 12} ${tenorMonths / 12 === 1 ? 'year' : 'years'}`
        : `${tenorMonths} months`
      : '—';

  const firstRepayPct = tenorMonths && grace && tenorMonths > 0 ? Math.min(90, (grace / tenorMonths) * 100) : 0;

  let prepayValue: ReactNode = <span className="opacity-50">Not detected</span>;
  if (prepayAllowed === true) {
    prepayValue = penalties.length > 0 ? `Allowed · ${penalties.map((p) => `${p}%`).join('/')}` : 'Allowed';
  } else if (prepayAllowed === false) {
    prepayValue = 'Not permitted';
  }

  return (
    <>
      <div className="bg-surface-subtle px-3 py-3">
        <div className="font-mono text-2xl font-semibold text-txt-primary">{tenorLabel}</div>
        <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-txt-tertiary">
          Tenor{tenorMonths !== null ? ` · ${tenorMonths} months` : ''}
        </div>
        {repayment && (
          <div className="mt-0.5 text-caption text-txt-tertiary">
            {capFirst(repayment)}
            {frequency ? ` · ${frequency}` : ''}
            {grace ? ` · ${grace}M grace` : ''}
          </div>
        )}
      </div>

      {/* Timeline — signed → first repayment → maturity */}
      {tenorMonths !== null && (
        <div className="mt-4 px-1">
          <div className="relative h-0.5 w-full bg-border">
            <div className="absolute inset-y-0 left-0 bg-teal/40" style={{ width: `${firstRepayPct}%` }} />
          </div>
          <div className="mt-1 flex justify-between text-[9px] uppercase tracking-wide text-txt-tertiary">
            <span>Signed</span>
            {grace ? <span>First repayment</span> : null}
            <span>Maturity</span>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:gap-1">
        <GridCell label="Repayment" value={repayment ? capFirst(repayment) : '—'} sub={frequency || undefined} />
        <GridCell label="Grace" value={grace ? `${grace} mo` : 'None'} sub={grace ? 'Interest-only' : undefined} />
        <GridCell label="Prepayment" value={prepayValue} />
      </div>
    </>
  );
}

function GridCell({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="flex-1 bg-surface-subtle px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">{label}</div>
      <div className="mt-0.5 font-mono text-body-sm font-medium text-txt-primary">{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-txt-tertiary">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
function ConditionsBody({ fields }: { fields: Fields }) {
  const financial = financialCovenantRows(fields);
  const operational = operationalCovenantRows(fields);
  const tf = testingFrequency(fields);

  return (
    <>
      <div className="mb-3 flex gap-1">
        <SummaryCell num={`${financial.length}`} label="Financial" />
        <SummaryCell num={`${operational.length}`} label="Operational" />
        <SummaryCell num={tf ? capFirst(tf) : '—'} label="Testing" />
      </div>
      <div>
        {financial.map((c, i) => (
          <div key={`f-${i}-${c.label}`} className="flex items-center gap-3 border-b border-border py-2 last:border-b-0">
            <span className="self-stretch w-[3px] shrink-0 bg-border" />
            <span className="flex-1 text-body-sm text-txt-primary">{c.label}</span>
            {c.threshold && <span className="font-mono text-body-sm text-txt-secondary">{c.threshold}</span>}
          </div>
        ))}
        {operational.map((c, i) => (
          <div key={`o-${i}-${c.label}`} className="flex items-center gap-3 border-b border-border py-2 last:border-b-0">
            <span className="self-stretch w-[3px] shrink-0 bg-border" />
            <span className="flex-1 text-body-sm text-txt-primary">{c.label}</span>
            <span className="text-caption uppercase text-txt-tertiary">Operational</span>
          </div>
        ))}
      </div>
    </>
  );
}

function SummaryCell({ num, label }: { num: string; label: string }) {
  return (
    <div className="flex-1 bg-surface-subtle px-2 py-2 text-center">
      <div className="font-mono text-lg font-semibold text-txt-primary">{num}</div>
      <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-txt-tertiary">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security
// ---------------------------------------------------------------------------
function SecurityBody({ fields }: { fields: Fields }) {
  const unsecured = isUnsecured(fields);
  const items = securityItems(fields);
  const pg = hasPersonalGuarantee(fields);

  return (
    <>
      <div className="bg-surface-subtle px-3 py-3">
        <div className="text-body font-semibold text-txt-primary">{unsecured ? 'Unsecured facility' : 'Secured facility'}</div>
        <div className="mt-0.5 text-body-sm text-txt-secondary">
          {unsecured
            ? 'No collateral requirements detected.'
            : pg
              ? 'Personal guarantee detected.'
              : 'No personal guarantee detected.'}
        </div>
      </div>
      {!unsecured && items.length > 0 && (
        <div className="mt-3">
          {items.map((it, i) => (
            <div key={`${i}-${it.label}`} className="flex items-start gap-2.5 border-b border-border py-2 last:border-b-0">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-orange" />
              <span className="flex-1 text-body-sm text-txt-primary">
                {it.label}
                {it.isInsurance && (
                  <span className="ml-2 border border-orange/30 bg-orange/10 px-1.5 py-0.5 align-middle text-[9px] font-semibold uppercase tracking-wide text-orange">
                    Market proxy
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Follow-up pills (diagnostic only — never advisory)
// ---------------------------------------------------------------------------
function pillsFor(compKey: CKey, pos: MarketPosition | null, fields: Fields): string[] {
  switch (compKey) {
    case 'pricing': {
      const out: string[] = [];
      if (pos === 'above median' || pos === 'below median') {out.push(`Why is my margin ${pos}?`);}
      if (f(fields, 'rate_floor_pct') !== null) {out.push('How does my rate floor work?');}
      out.push('What drives margin pricing?');
      return out.slice(0, 3);
    }
    case 'fee_load':
      return ['Is this fee structure typical?', 'What fees should I watch for?'];
    case 'structure':
      return ['What are my prepayment options?', 'How does the grace period affect the loan?'];
    case 'conditions':
      return ['How tight is my leverage covenant?', 'What happens if I breach a covenant?', 'Are these thresholds standard?'];
    case 'security':
      return ['What does the security package mean for me?', 'Is no personal guarantee unusual?'];
    default:
      return [];
  }
}
